
import { useEffect, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getCarlines, getDataUrl, getSalesgroups } from 'src/bff';
import { OneFormContent, useFaContent, useFeatureServices } from 'src/feature-app';
import { findCarlineFromSalesgroup, findCarline, isCarlineMode } from 'src/components';
import { CarlineBase, CarlinesWithSalesgroups, SalesGroupInfo, Steps } from 'src/types';
import { ConfigurationWithBuildabilityV2 } from '@volkswagen-onehub/car-configuration-feature-service';
import { useIsComerciales } from 'src/feature-app/hooks';
import { removeStep, resetStepsScreenIndex } from '../helpers/add-remove-steps';
import { getMarketingCode } from 'src/bff';


const getLastItemOfSteps = (steps: Steps[]) => steps[steps.length - 1];
const carlineInFaContent = (faContent: OneFormContent) => faContent?.carlines && faContent.carlines.length === 1;
const salesgroupInFaContent = (faContent: OneFormContent) =>
  faContent?.salesgroups && faContent.salesgroups.length === 1;
const findSalesgroup = (carline: CarlinesWithSalesgroups, salesgroupId: string) =>
  carline?.salesGroups?.find((salesgroup) => salesgroup.code === Number(salesgroupId));

interface LeadsInitialConfig {
  loading: boolean;
  steps: Steps[];
  firstStep: Steps;
  lastStep: Steps;
}

const updateSteps = (steps: Steps[]) => {
  return {
    type: 'UPDATE_STEPS',
    payload: steps,
  };
};

const reducer = (state: LeadsInitialConfig, action: any): LeadsInitialConfig => {
  switch (action.type) {
    case 'UPDATE_STEPS':
      return {
        ...state,
        steps: action.payload,
        firstStep: action.payload[0],
        lastStep: getLastItemOfSteps(action.payload),
        loading: false,
      };
    default:
      return state;
  }
};

const initialState: LeadsInitialConfig = {
  loading: true,
  steps: [],
  firstStep: null,
  lastStep: null,
};

export const createSalesgroup = (code: string | number, name: string) => {
  if (!code && !name) {
    return null;
  }
  return { salesGroupId: String(code), salesGroupName: String(name) };
};

export function useInitializeLeadForm(
  defaultSteps: Steps[],
  isOfertasOrMatch?: boolean,
  configRef?: ConfigurationWithBuildabilityV2,
  isConfigurador?: boolean,
) {
  const dispatch = useDispatch();
  const faContent = useFaContent();
  const { 'car-configuration': carConfigurationService } = useFeatureServices();
  const [state, dispatchLead] = useReducer(reducer, initialState);
  const { loading, firstStep, lastStep, steps } = state;
  const isComerciales = useIsComerciales();

  const requestCarlines = async () => {
    let carlines;

    if (faContent?.mode === 'Carline') {
      carlines = await getCarlines();
    } else {
      carlines = await getSalesgroups();
    }

    dispatch({ type: 'SET_CARLINES', payload: carlines });

    return carlines;
  };

  const getCarlineAndSalesgroup = (carlines: CarlineBase[]): { carline: CarlineBase; salesgroup: SalesGroupInfo } => {
    let carlineCode: string = carConfigurationService.get().carlineId;
    let salesgroupId = carConfigurationService.get().salesGroupId;

    if ((isOfertasOrMatch && faContent?.oferta) || (isOfertasOrMatch && faContent?.match)) {
      // Formulario de ofertas o match
      let offerOrPreconfiguration = faContent?.oferta ? faContent.oferta : faContent.match;

      const carline = findCarline(carlines, offerOrPreconfiguration.carlineCode);
      const salesgroup = createSalesgroup(
        faContent.marketingCodeConfig?.SalesgroupId,
        faContent.marketingCodeConfig?.SalesgroupName
      ); // puede que no sea necesaria esta parte, también se hace la request del marketing code al momento del envío.

      dispatch({ type: 'SET_OFERTA', payload: offerOrPreconfiguration });

      return { carline, salesgroup };
    } else if (configRef) {
      // Formulario configurador
      const carline = findCarline(carlines, Number(configRef.carlineId));
      const salesgroup = createSalesgroup(configRef.salesGroupId, '');

      return { carline, salesgroup };
    } else if (carlineCode && salesgroupId) {
      // Showroom
      const carline = findCarline(carlines, Number(carlineCode), Number(salesgroupId));
      const salesgroupCarline = findSalesgroup(carline, salesgroupId);
      const salesgroup = createSalesgroup(salesgroupCarline?.code, salesgroupCarline?.name);

      return { carline, salesgroup };
    } else if (carlineInFaContent(faContent)) {
      const carline = findCarline(carlines, Number(faContent.carlines[0]));

      return { carline, salesgroup: null };
    } else if (salesgroupInFaContent(faContent) && !isCarlineMode(faContent)) {
      const carline = findCarlineFromSalesgroup(carlines, Number(faContent.salesgroups[0]));
      const salesgroupCarline = findSalesgroup(carline, String(faContent.salesgroups[0]));
      const salesgroup = createSalesgroup(salesgroupCarline?.code, salesgroupCarline?.name);

      return { carline, salesgroup };
    }
    return { carline: null, salesgroup: null };
  };

  const removeStepAndReset = (steps: Steps[], stepName: string) => {
    let updatedSteps = [...steps];
    updatedSteps = removeStep(steps, stepName);
    updatedSteps = resetStepsScreenIndex(steps);
    return updatedSteps;
  };

  //inicio de leadInPage

  const findAsset = (mediaAssets: any[], assetName: string) =>
    mediaAssets.find((asset) => asset.mpsId === assetName);

  const getAsset = (
    mediaAssets: any[],
    assetName: string
  ): string => {
    const asset = findAsset(mediaAssets, assetName);
    let assetUrl = asset.asset;

    if (assetUrl.includes('r-media') && assetUrl.includes('.jpg')) {
      assetUrl = assetUrl.replace('.jpg', '.png');
    }

    return assetUrl;
  };

  const useLeadsWithMarketingCode = (marketingCode: string) => {
    getMarketingCode(marketingCode)
      .then((res) => {
        const colorCode = res.response.consolidatedData?.exterior.code;
        const D6MOFASideview = getAsset(
          res.response.consolidatedData?.mediaAssets,
          'D6MOFASideview'
        );
        const D6Explore1 = getAsset(
          res.response.consolidatedData?.mediaAssets,
          'D6Explore1'
        );
        const modelName = res.response.consolidatedData?.modelName;
        const marketingCodeData = { marketingCode, modelName, colorCode, mediaAssets: { D6MOFASideview, D6Explore1 } };

        let finalSteps: Steps[] = [...defaultSteps];

        finalSteps = removeStepAndReset(finalSteps, 'Modelo');

        if (!isComerciales) {
          finalSteps = removeStepAndReset(finalSteps, 'CodigoPostal');
        }

        dispatchLead(updateSteps(finalSteps));

        dispatch({ type: 'SET_DELETE_CLOSE_AND_PREVIOUS', payload: true });

        dispatch({
          type: 'UPDATE_FIELDS',
          payload: { marketingCodeData, marketingCodeResponse: res.response },
        });

      })
      .catch((err) => {
        requestCarlines().then((carlines) => {
          const { carline, salesgroup } = getCarlineAndSalesgroup(carlines);

          let finalSteps: Steps[] = [...defaultSteps];

          if (!isComerciales) {
            finalSteps = removeStepAndReset(finalSteps, 'CodigoPostal');
          }
          if (carline) {
            finalSteps = removeStepAndReset(finalSteps, 'Modelo');

            dispatch({ type: 'SET_SELECTED_CARLINE', payload: carline });
            dispatch({
              type: 'SET_SALESGROUP',
              payload: salesgroup,
            });
            dispatch({
              type: 'UPDATE_FIELDS',
              payload: { modelo: carline.title },
            });
          }
          dispatchLead(updateSteps(finalSteps));
        });
        // console.log(err, 'error');
      });

  };

  const useLeadsWithModel = async (model: string, salesGroup?: string) => {
    await getModelInfo(model)
      .then((res) => {
        let colorCode = res.carline.colores.exterior.hexadecimal;
        let D6MOFASideview: string, D6Explore1: string, modelName: string, salesGroupData: any;
        const carlineCode: number = res.carline.code;

        const setDataNoSalesGroup = () => {
          D6MOFASideview = res.carline.mediaAssets.D6MOFASideview;
          D6Explore1 = res.carline.mediaAssets.D6MainStageExterior;
          modelName = res.carline.title;
        }

        if (salesGroup) {
          const salesGroupCoincidences = res.carline.salesgroups.filter((e) => (e.name === salesGroup));
          salesGroupData = salesGroupCoincidences[0];
        }
        if (salesGroupData) {
          try {
            D6MOFASideview = salesGroupData.mediaAssets.D6MOFASideview;
            D6Explore1 = salesGroupData.mediaAssets.D6MainStageExterior;
            colorCode = salesGroupData.colores?.exterior?.hexadecimal;
            modelName = salesGroupData.title;
          } catch (error) {
            console.error('No data for salesgroup found');
            setDataNoSalesGroup();
          }
        }
        else {
          setDataNoSalesGroup();
        }

        const carlineData = { modelName, colorCode, mediaAssets: { D6MOFASideview, D6Explore1 } };

        let finalSteps: Steps[] = [...defaultSteps];

        finalSteps = removeStepAndReset(finalSteps, 'Modelo');

        if (!isComerciales) {
          finalSteps = removeStepAndReset(finalSteps, 'CodigoPostal');
        }

        dispatchLead(updateSteps(finalSteps));

        dispatch({ type: 'SET_DELETE_CLOSE_AND_PREVIOUS', payload: true });

        dispatch({
          type: 'UPDATE_FIELDS',
          payload: { carlineData, carlineResponse: res, carlineCode, modelo: modelName },
        });

        dispatch({ type: 'SET_SELECTED_CARLINE', payload: res.carline });
        if (salesGroupData) {
          dispatch({
            type: 'SET_SALESGROUP',
            payload: { salesGroupId: salesGroupData.code, salesGroupName: salesGroupData.title },
          });
        }

      })
      .catch((err) => {
        requestCarlines().then((carlines) => {
          const { carline, salesgroup } = getCarlineAndSalesgroup(carlines);

          let finalSteps: Steps[] = [...defaultSteps];

          if (!isComerciales) {
            finalSteps = removeStepAndReset(finalSteps, 'CodigoPostal');
          }
          if (carline) {
            finalSteps = removeStepAndReset(finalSteps, 'Modelo');

            dispatch({ type: 'SET_SELECTED_CARLINE', payload: carline });
            dispatch({
              type: 'SET_SALESGROUP',
              payload: salesgroup,
            });
            dispatch({
              type: 'UPDATE_FIELDS',
              payload: { modelo: carline.title },
            });
          }
          dispatchLead(updateSteps(finalSteps));
        });
        // console.log(err, 'error');
      });

  };

  const getModelInfo = async (model: string) => {
    let dataRes: any;
    await fetch(`${getDataUrl()}/getCarlineSalesgroups/${model}`)
      .then(res => res.json())
      .then(data => dataRes = data)
    return dataRes
  }

  const getModelFromUrl = () => {
    const path = window.location.pathname;
    let htmlIndex: number;
    let carlineName: string;
    let salesGroup: string;
    const splittedPath = path.split("/");
    splittedPath.filter((e, index) => e.includes(".html") && (htmlIndex = index));
    carlineName = splittedPath[htmlIndex + 1]?.replace(".app", "");

    const hasSalesGroup = () => splittedPath[htmlIndex + 2]?.includes(".app");
    hasSalesGroup() && (salesGroup = splittedPath[htmlIndex + 2]?.replace(".app", ""));
    return [carlineName, salesGroup]
  }

  const ofertasUrl = () => {
    const path = window.location.pathname;
    return path.includes('ofertas')
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const marketingCode = urlParams.get('marketingCode');

    if (marketingCode) {
      useLeadsWithMarketingCode(marketingCode);

    } else {
      const [carline, salesGroup] = getModelFromUrl();
      if (carline && !isOfertasOrMatch) useLeadsWithModel(carline, salesGroup)
      else {
        requestCarlines().then((carlines) => {
          const { carline, salesgroup } = getCarlineAndSalesgroup(carlines);

          let finalSteps: Steps[] = [...defaultSteps];

          if (!isComerciales) {
            finalSteps = removeStepAndReset(finalSteps, 'CodigoPostal');
          }
          if (carline) {
            finalSteps = removeStepAndReset(finalSteps, 'Modelo');

            dispatch({ type: 'SET_SELECTED_CARLINE', payload: carline });
            dispatch({
              type: 'SET_SALESGROUP',
              payload: salesgroup,
            });
            dispatch({
              type: 'UPDATE_FIELDS',
              payload: { modelo: carline.title },
            });
          }
          dispatchLead(updateSteps(finalSteps));
        });
      }
    }

  }, []);

  return { loading, steps, firstStep, lastStep };
}
